import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import Input from "../utils/Input";
import Button from "../utils/Button";
import { useLogin } from "../hooks/useLogin";
import MessagingDisclosureModal from "./MessagingDisclosureModal";
import { useMessagingDisclosure } from "../hooks/useMessagingDisclosure";
import { useApiQuery } from "../hooks/useApiQuery";
import toast from "react-hot-toast";
import { request } from "../utils/api";
import {
  AUTH_REQUIRED_TOAST_ID,
  AUTH_ROLE_TOAST_ID,
  buildApiQueryKey,
  clearStoredAuth,
  extractLoginRole,
  extractLoginToken,
  extractLoginUser,
  getAuthQueryKey,
  getCurrentUserRequest,
  getStoredToken,
  setStoredRole,
  setStoredToken,
} from "../utils/auth";
import { getRoleDefaultPath, getRoleLoginPath, UserRole } from "../utils/roleUtils";


export default function LoginLayout({ role }: { role?: string }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showDisclosureModal, setShowDisclosureModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutateAsync: login, isPending, error } = useLogin();
  const loadingToastId = useRef<string | null>(null);

  // Get partner data for disclosure modal
  const hasExistingSession = !!getStoredToken();
  const { data: partnerData } = useApiQuery<{
    data: { partnerIdentifier: string; name: string };
  }>({
    queryKey: ["partner", "me"],
    request: { method: "GET", url: "/partner" },
    enabled: role?.toLowerCase() === "partner" && hasExistingSession,
  });

  const partnerId = partnerData?.data?.partnerIdentifier;
  const { shouldShowDisclosure, acknowledgeDisclosure } = useMessagingDisclosure(partnerId);

  useEffect(() => {
    toast.dismiss(AUTH_REQUIRED_TOAST_ID);
    toast.dismiss(AUTH_ROLE_TOAST_ID);
  }, []);

  useEffect(() => {
    if (isPending && !loadingToastId.current) {
      loadingToastId.current = toast.loading('Logging in...');
    }

    if (!isPending && loadingToastId.current) {
      toast.dismiss(loadingToastId.current);
      loadingToastId.current = null;
    }

    return () => {
      if (loadingToastId.current) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = null;
      }
    };
  }, [isPending]);

  // Show disclosure modal for partners after successful login
  useEffect(() => {
    if (
      role?.toLowerCase() === "partner" &&
      partnerId &&
      shouldShowDisclosure() &&
      !isPending
    ) {
      setShowDisclosureModal(true);
    }
  }, [role, partnerId, shouldShowDisclosure, isPending]);

  const handleEnableMessaging = () => {
    acknowledgeDisclosure();
    toast.success('Messaging enabled for your organization');
  };

  const handleCloseModal = () => {
    setShowDisclosureModal(false);
  };

  const getRoleHint = (): UserRole => {
    switch ((role || "").toLowerCase()) {
      case "admin":
        return "SYSTEM_ADMIN";
      case "partner":
        return "PARTNER";
      case "supervisor":
        return "SUPERVISOR";
      case "canvasser":
        return "CANVASSER";
      default:
        return "CANVASSER";
    }
  };

  const getTargetPath = (roleHint: UserRole) => {
    return getRoleDefaultPath(roleHint);
  };

  const isAuthorizedForLoginRoute = (actualRole: UserRole, expectedRole: UserRole) => {
    if (expectedRole === "PARTNER") {
      return actualRole === "PARTNER" || actualRole === "PARTNER_ADMIN";
    }

    return actualRole === expectedRole;
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      const response = await login({ email, password });
      const expectedRole = getRoleHint();
      const actualRole =
        extractLoginRole(response) ||
        extractLoginUser(response)?.role;
      const token = extractLoginToken(response);
      const loginUser = extractLoginUser(response);

      if (!actualRole) {
        clearStoredAuth();
        throw new Error("Unable to verify the account role for this login.");
      }

      if (!isAuthorizedForLoginRoute(actualRole, expectedRole)) {
        clearStoredAuth();
        toast.dismiss(AUTH_REQUIRED_TOAST_ID);
        toast.dismiss(AUTH_ROLE_TOAST_ID);
        toast.error("You are not authorized for this dashboard.");
        navigate(getRoleLoginPath(expectedRole), { replace: true });
        return;
      }

      if (!token) {
        clearStoredAuth();
        throw new Error("Login succeeded but no token was returned.");
      }

      setStoredToken(token);
      setStoredRole(actualRole);

      // Clear all cached queries to prevent data from previous accounts from persisting
      queryClient.clear();

      await queryClient.fetchQuery({
        queryKey: buildApiQueryKey(getAuthQueryKey(actualRole)),
        queryFn: () => request(getCurrentUserRequest(actualRole)),
      });

      toast.dismiss(AUTH_REQUIRED_TOAST_ID);
      toast.dismiss(AUTH_ROLE_TOAST_ID);
      toast.success('Login successful');
      navigate(getTargetPath(loginUser?.role || actualRole), { replace: true });
    } catch (err: any) {
      clearStoredAuth();
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Login failed. Please try again.';
      toast.error(message);
    }
  };

  return (
    <>
      <form
        onSubmit={handleLogin}
        className="bg-gray-900 p-8 rounded-lg shadow-lg w-full max-w-md animate-fireGlow"
      >
        <h2 className="text-2xl text-white font-bold mb-6 text-center">{role} Login</h2>
        {/* {error && <p className="text-red-500 text-sm mb-4">{error}</p>} */}
        <Input
          label="Email"
          name="email"
          placeholder="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          debounce={0}
          required
          className="mb-4"
          onValidate={(val) => {
            if (!val) return 'Email is required';
            const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
            return ok ? null : 'Invalid email address';
          }}
        />

        <Input
          label="Password"
          placeholder="Password"
          type="password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          debounce={0}
          required
          className="mb-6"
          helper="Minimum 8 characters"
          onValidate={(val) => (val && val.length >= 8 ? null : 'Password must be at least 8 characters')}
        />

        <Button label="Login" type="submit" variant="primary" size="extra-large" />
        <div className="mt-8 pt-6 border-t border-gray-700">
          <div className="text-center">
            <p className="text-sm font-medium text-gray-300 mb-4">
              Not {role?.toLowerCase() == "admin" ? "an" : "a"} {role?.toLowerCase()}?
            </p>
            <div className="inline-flex items-center gap-1 p-1 bg-gray-800 rounded-xl shadow-inner">
              {role?.toLowerCase() !== 'supervisor' && (
                <Link
                  to="/supervisor/login"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-purple-600/20 hover:text-purple-300 rounded-lg transition-all duration-200"
                >
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.414l.707-.707zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  Supervisor
                </Link>
              )}
              {role?.toLowerCase() !== 'partner' && (
                <Link
                  to="/partner/login"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-blue-600/20 hover:text-blue-300 rounded-lg transition-all duration-200"
                >
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                    </svg>
                  </div>
                  Partner
                </Link>
              )}
              {role?.toLowerCase() !== 'canvasser' && (
                <Link
                  to="/canvasser/login"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-300 hover:text-white hover:bg-green-600/20 hover:text-green-300 rounded-lg transition-all duration-200"
                >
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  Canvasser
                </Link>
              )}


            </div>
          </div>
              <div className="text-xs text-slate-400 text-center mt-4">Cannot gain access? <Link className="underline text-blue-500" to="/forgot-password">Forgot Password</Link></div>
        </div>
      </form>


      {/* Messaging Disclosure Modal for Partners
      <MessagingDisclosureModal
        isOpen={showDisclosureModal}
        onClose={handleCloseModal}
        onEnableMessaging={handleEnableMessaging}
        partnerId={partnerId}
      /> */}
    </>
  );
}
